import { resolveExampleImageGameName } from '../../../api/blueprintFamilies/exampleImages'
import type { BlueprintFamilyExampleImage } from '../../../api/blueprintFamilies/exampleImages'
import { useGetGameQuery, useGetTrackedGamesQuery } from '../../../api/core'
import { LockedGameSummary } from '../../../types/LockedFeature'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { AppType } from '../../game/types/Game'
import { useCurrentMarket } from '../../markets'

/**
 * The source game of an analyst-curated example screenshot (Blueprint Family surfaces), looked
 * up for its icon and publisher, plus the `LockedGameSummary` the unlock dialog shows for it.
 *
 * Shared by the caption under the image (`LiveEventSourceCaption`) and by the image click
 * handlers that open the unlock dialog for a game outside the user's plan, so both show the
 * same game. RTK Query dedupes the lookups by args, so any number of callers for the same game
 * cost one request.
 *
 * `enabled` gates the lookups: nothing is fetched while it is `false`, and `game` and the
 * `lockedGame` icon/publisher stay empty.
 */
export const useExampleImageGame = (image: BlueprintFamilyExampleImage, enabled: boolean) => {
  const userLanguage = useCurrentUserLanguage()
  const { currentMarketIso } = useCurrentMarket()

  const gameName = resolveExampleImageGameName(image, userLanguage)

  // The `gameOnly: true` / `tags: false` shape is the same lightweight "icon-only"
  // request the game search results use — not `getGames` (plural, no query
  // params): that endpoint 500s without them, and the error response comes back
  // with no CORS header, which surfaces in the browser as an opaque failed fetch.
  // Requesting a single id (rather than lifting this to the gallery and batching
  // every screenshot's `gameId` in one call) keeps this self-contained;
  // RTK Query still dedupes callers that share a game down to one request and one
  // cache entry, so a multi-shot gallery from a handful of games doesn't fan out
  // into one request per screenshot.
  const wantsGame = Boolean(enabled && image.gameId && currentMarketIso)
  const { currentData: mobileGame, isFetching: isFetchingMobileGame } = useGetGameQuery(
    { id: image.gameId, marketIso: currentMarketIso, tags: false, gameOnly: true, userLanguage },
    { skip: !wantsGame }
  )

  // The single-game lookup above only serves mobile games, so a PC/console game comes
  // back empty and fell through to the placeholder art. The tracked-games list covers
  // both kinds and is the source the rest of the live-event surfaces already read
  // PC/console games from; it is one shared request for every caller on the page, and
  // is only asked for once the single-game lookup has settled without an icon. Not gated on
  // the Live Events Tracker role: the list requires none, and lists every tracked game
  // (allowed or not) to free users too, who now reach these captions in the Data Glossary.
  const needsTrackedGameFallback = wantsGame && !isFetchingMobileGame && !mobileGame?.getIcon()
  const { data: trackedGames } = useGetTrackedGamesQuery({ userLanguage }, { skip: !needsTrackedGameFallback })
  const trackedGame = needsTrackedGameFallback ? trackedGames?.find((candidate) => candidate.game?.id === image.gameId)?.game : undefined
  const game = mobileGame?.getIcon() ? mobileGame : trackedGame ?? mobileGame

  // The platform badge on the game icon. The single-game lookup only serves mobile games, so a game it
  // returned is mobile even when the payload carries no `appType`; a PC/console game only ever arrives
  // through the tracked-games fallback, which does.
  const appType = game?.appType ?? (mobileGame ? AppType.MOBILE : undefined)
  const platforms = game?.platforms

  const lockedGame: LockedGameSummary = {
    name: gameName || game?.resolvedName || '',
    artist: game?.artist || undefined,
    iconUrl: (game?.getIcon() as string | undefined) || undefined,
  }

  return { game, appType, platforms, lockedGame }
}
